import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FileSpreadsheet, Flag, Users, FolderOpen, Plus, Settings, LogOut, Send, CalendarDays, FileText, Menu, X, Info } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Sidebar = () => {
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#4F46E5'
  });

  useEffect(() => {
    if (isAdmin()) {
      fetchTeamMembers();
      fetchAccounts();
    }
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team-members`);
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts`);
      setAccounts(response.data);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/accounts`, formData);
      toast.success('Account created successfully');
      setDialogOpen(false);
      setFormData({ name: '', description: '', color: '#4F46E5' });
      fetchAccounts();
    } catch (error) {
      console.error('Error creating account:', error);
      toast.error('Failed to create account');
    }
  };

  const navItems = [
    { to: `/team/${user?.id}`, icon: LayoutDashboard, label: 'My Tasks', adminOnly: false, memberOnly: true },
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', adminOnly: true },
    { to: '/ra-sheet', icon: FileSpreadsheet, label: 'Task Management', adminOnly: true },
    { to: '/priority-tasks', icon: Flag, label: 'Priority Tasks', adminOnly: true },
    { to: '/client-deliverables', icon: Send, label: 'Client Deliverables', adminOnly: true },
    { to: '/team-management', icon: Settings, label: 'Team Settings', adminOnly: true },
    { to: '/reports', icon: FileText, label: 'Reports', adminOnly: true },
    { to: '/holiday-calendar', icon: CalendarDays, label: 'Holiday Calendar', adminOnly: false },
    { to: '/work-cards', icon: FolderOpen, label: 'Division Wise Work Cards (Workflows)', adminOnly: false },
    { to: '/important-information', icon: Info, label: 'Important Information', adminOnly: false },
  ];

  return (
    <>
      {/* Mobile hamburger button - only visible on small screens */}
      <button
        onClick={() => setMobileMenuOpen(true)}
        className="md:hidden fixed top-4 left-4 z-40 p-2 bg-white border border-border rounded-lg shadow-sm"
        data-testid="mobile-menu-toggle"
      >
        <Menu className="h-5 w-5" />
      </button>
      {/* Mobile overlay backdrop - only shown when menu is open */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      <aside className={`w-64 fixed h-full border-r border-border bg-white z-50 overflow-y-auto transition-transform duration-200 ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      } md:translate-x-0`}>
        <button
          onClick={() => setMobileMenuOpen(false)}
          className="md:hidden absolute top-4 right-4 p-1 hover:bg-muted rounded"
        >
          <X className="h-5 w-5" />
        </button>
      <div className="p-6">
        <div className="flex items-center gap-3 justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/logo.png" 
              alt="Content Logo" 
              className="h-10 w-auto"
            />
            <div>
              <p className="text-xs text-muted-foreground">{user?.name}</p>
            </div>
          </div>
        </div>
      </div>

      <nav className="px-3 space-y-1">
        {navItems.filter(item => (!item.adminOnly || isAdmin()) && (!item.memberOnly || !isAdmin())).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${
                isActive
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`
            }
          >
            <item.icon className="h-5 w-5" />
            <span className="text-sm">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Accounts Section - Admin Only */}
      {isAdmin() && (
      <div className="mt-8 px-3">
        <div className="flex items-center justify-between px-3 mb-3">
          <div className="flex items-center gap-2">
            <FolderOpen className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Accounts</span>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                size="sm" 
                variant="ghost" 
                className="h-6 w-6 p-0 hover:bg-primary/10"
                data-testid="add-account-sidebar-btn"
              >
                <Plus className="h-4 w-4 text-primary" />
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white" data-testid="account-dialog-sidebar">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">Create New Account</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="name" className="text-sm font-semibold">Account Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-1.5 bg-white"
                    placeholder="e.g., BharatREVOLT"
                  />
                </div>
                <div>
                  <Label htmlFor="description" className="text-sm font-semibold">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-1.5 bg-white"
                    placeholder="Brief description"
                    rows={2}
                  />
                </div>
                <div>
                  <Label htmlFor="color" className="text-sm font-semibold">Color Tag</Label>
                  <Input
                    id="color"
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="mt-1.5 h-10 bg-white"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-primary hover:bg-primary/90">
                    Create Account
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        <div className="space-y-1">
          <NavLink
            to="/accounts"
            data-testid="nav-all-accounts"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                isActive
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`
            }
          >
            <FolderOpen className="h-4 w-4" />
            <span className="text-sm">All Accounts</span>
          </NavLink>
          {accounts.slice(0, 5).map((account) => (
            <NavLink
              key={account.id}
              to={`/accounts/${account.id}`}
              data-testid={`nav-account-${account.name.toLowerCase().replace(/\\s+/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`
              }
            >
              <div 
                className="h-3 w-3 rounded-full" 
                style={{ backgroundColor: account.color }}
              />
              <span className="text-sm truncate flex-1">{account.name}</span>
              <span className="text-xs text-muted-foreground">{account.taskCount}</span>
            </NavLink>
          ))}
        </div>
      </div>
      )}

      {/* Team Section - Admin Only */}
      {isAdmin() && (
      <div className="mt-8 px-3 pb-6">
        <div className="flex items-center gap-2 px-3 mb-3">
          <Users className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Team</span>
        </div>
        <div className="space-y-1">
          {teamMembers.map((member) => (
            <NavLink
              key={member.id}
              to={`/team/${member.id}`}
              data-testid={`nav-team-${member.name.toLowerCase().replace(' ', '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md transition-colors relative ${
                  isActive
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`
              }
            >
              <div className="relative">
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary/20 to-primary/40 flex items-center justify-center text-sm font-semibold">
                  {member.name.charAt(0)}
                </div>
                {member.laggingTaskCount > 0 && (
                  <div className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-red-500 ring-2 ring-white animate-pulse" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{member.name}</p>
                <p className="text-xs text-muted-foreground">{member.taskCount} tasks</p>
              </div>
            </NavLink>
          ))}
        </div>
      </div>
      )}

      {/* Logout Button */}
      <div className="mt-auto px-3 py-6 border-t">
        <Button
          onClick={() => {
            logout();
            navigate('/login');
          }}
          variant="outline"
          className="w-full flex items-center justify-center gap-2"
        >
          <LogOut className="h-4 w-4" />
          <span>Logout</span>
        </Button>
      </div>
    </aside>
    </>
  );
};

export default Sidebar;
